import {
  CloseOutlined,
  MessageOutlined,
  CustomerServiceOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  StarOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { Drawer, Avatar, Typography } from "antd";
import { Collapse, List, Switch } from "antd-mobile";
import { useAppNavigate, TMobileRouterPath } from "../../../hooks/router";
import { routes } from "../../../router/mobile/routes";
import { SupportItem } from "../../CustomComponents/SupportItem";
import styles from "./styles.module.scss";
import { ConfirmModal } from "../../CustomComponents/ConfirmModal/index.mobile";
import { IAnalyticsController } from "../../../hexagon/adapters/driver/analyticsController";
import { analyticsEventSettingsPaths } from ".";
import { useState } from "react";
import { getOperatingSystem } from "../../../util/common";
import { useShouldShowMaskToggle } from "../../../hooks/useShouldShowMaskToggle";
import classNames from "classnames";
import { DownOutline, RightOutline } from "antd-mobile-icons";

const { Text } = Typography;
const year = new Date().getFullYear();

export interface IMobileLeftMenu {
  open: boolean;
  onToggle: (e?: { stopPropagation: () => void }) => void;
  onLogout: () => void;
  user?: User;
  userClinic?: IClinic;
  confirmLogout: boolean;
  onCancel: () => void;
  onConfirmLogout: () => void;
  analyticsController: IAnalyticsController;
  circadiaEngineProvider: ICircadiaEngineProvider;
  localBetaFeature: boolean;
  onLocalBetaFeature: (value: boolean) => void;
  /** Pre-computed by the parent so this component stays Redux-free and testable without a Provider. */
  showNpSchedulingLink?: boolean;
  /** Pre-computed by the parent (true for VNS clinics). */
  hideVitalsAiLink?: boolean;
}

export const MobileLeftMenu: FC<IMobileLeftMenu> = (props) => {
  const {
    open,
    onToggle,
    user,
    userClinic,
    onLogout,
    confirmLogout,
    onCancel,
    onConfirmLogout,
    analyticsController,
    circadiaEngineProvider,
    showNpSchedulingLink,
    hideVitalsAiLink,
  } = props;

  const navigate = useAppNavigate();
  const storedBiometricValue = localStorage.getItem("isBiometric");
  const isBiometric = storedBiometricValue && JSON.parse(storedBiometricValue);
  const [isSwitchBio, setIsSwitchBio] = useState(isBiometric);
  const isNotAndroid =
    getOperatingSystem({ circadiaEngineProvider }) !== "android";
  const showMaskToggle = useShouldShowMaskToggle();

  // Get initial toggle state from localStorage (defaults to true)
  const getInitialMaskState = (): boolean => {
    const stored = localStorage.getItem("maskPatientNames");
    return stored === null ? true : stored === "true";
  };

  // Mask patient names toggle state
  const [maskPatientNames, setMaskPatientNames] =
    useState<boolean>(getInitialMaskState);

  // Handle toggle change
  const handleMaskToggleChange = (checked: boolean) => {
    setMaskPatientNames(checked);
    localStorage.setItem("maskPatientNames", checked.toString());
    setTimeout(() => {
      window.location.reload(); // Necessary because the page underneath the sidebar needs to be redrawn.  This is a different situation than the UserProfile settings on tablet/desktop, which is its own page with no patient names on it (nothing that needs to be reloaded).
    }, 700); // wait for toggle switch animation to finish
  };

  const onRouteChange = (route: TMobileRouterPath) => {
    const event: string | undefined = analyticsEventSettingsPaths[route];
    if (event) {
      analyticsController.logEvent(event, {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    navigate(route, {
      state: {
        previousRoute: "Settings",
      },
    });
    onToggle();
  };

  return (
    <>
      <Drawer
        width={285}
        placement="left"
        closable={false}
        zIndex={1031}
        onClose={onToggle}
        open={open}
        destroyOnClose
        className={styles.drawerContainer}
      >
        <div className={styles.menuHeaderContainer}>
          <h3 id="main-heading">Settings</h3>
          <CloseOutlined
            className={styles.viewIcon}
            onClick={onToggle}
            data-test-id="close-icon"
          />
        </div>
        <div className={styles.userBlock}>
          <Avatar
            className={styles.avatar}
            size={40}
            shape="square"
            data-test-id="avatar"
          >
            {`${user?.firstName?.charAt(0).toUpperCase()}${user?.lastName
              ?.charAt(0)
              .toUpperCase()}`}
          </Avatar>
          <h6
            data-test-id="user-name"
            className={styles.name}
          >{`${user?.firstName} ${user?.lastName}`}</h6>
        </div>
        <div className={`${styles.clinicBlock} mb-4`}>
          <p data-test-id="facility-name" className={styles.facilityName}>
            {userClinic?.name}{" "}
          </p>
          <p
            className={styles.changeClinic}
            onClick={() => onRouteChange(routes.changeFacility.path)}
            id="change-facility"
            data-test-id="change-facility"
          >
            Change
          </p>
        </div>
        {/* NP Scheduling: visible for internal NPs and np-supervisor users.
            Computed by the parent (MobileLayout) via shouldShowNpSchedulingSidebarLink
            so this component stays Redux-free and testable without a Provider. */}
        {showNpSchedulingLink && (
          <div
            className={`${styles.menuItem} mb-3`}
            onClick={() => onRouteChange(routes.npScheduling.path)}
            id="np-scheduling"
          >
            <h6 className={styles.menuItemTitle} id="np-scheduling-title">
              Scheduling
            </h6>
            <RightOutline className={styles.menuIcon} />
          </div>
        )}
        <Collapse
          className={`mb-3 ${styles.collapse}`}
          style={{ backgroundColor: "white" }}
          arrow={(active) =>
            active ? (
              <DownOutline className={classNames(styles.menuIcon)} />
            ) : (
              <RightOutline className={classNames(styles.menuIcon)} />
            )
          }
        >
          <Collapse.Panel
            key="1"
            title={
              <h6
                className={styles.menuItemTitle}
                id="user-profile-settings-title"
              >
                User Profile
              </h6>
            }
            data-test-id="user-profile"
            onClick={() =>
              analyticsController.logEvent("Tap on user profile tab", {
                email: user?.email,
                selectedFacility: userClinic?.name,
                selectedFacilityId: userClinic?.id,
                cUserId: user?.userID,
              })
            }
          >
            <List className={styles.profileList}>
              <List.Item
                clickable
                className={styles.textStyle}
                arrow={false}
                onClick={() => onRouteChange(routes.changePassword.path)}
                data-test-id="change-password"
              >
                Change Password
              </List.Item>
              <List.Item
                clickable
                className={styles.textStyle}
                arrow={false}
                onClick={() => onRouteChange(routes.changePin.path)}
                data-test-id="change-pin"
              >
                Change PIN
              </List.Item>
            </List>
          </Collapse.Panel>
        </Collapse>
        <div
          className={`${styles.menuItem} mb-3`}
          onClick={() => onRouteChange(routes.floorSettings.path)}
          id="floor-selection"
        >
          <h6
            className={styles.menuItemTitle}
            id="floor-selection-settings-title"
          >
            Floor/Unit Selection
          </h6>
          <RightOutline className={styles.menuIcon} />
        </div>
        <div
          className={`${styles.menuItem} mb-3`}
          onClick={() => onRouteChange(routes.notificationSettings.path)}
          id="notification-settings"
        >
          <h6
            className={`${styles.menuItemTitle}`}
            id="notification-settings-title"
          >
            Notification Preferences
          </h6>
          <RightOutline className={styles.menuIcon} />
        </div>
        <div
          className={`${styles.menuItem} mb-3`}
          onClick={() => onRouteChange(routes.facilityInformation.path)}
          id="facility-information"
          data-test-id="facility-information"
        >
          <h6
            className={styles.menuItemTitle}
            id="facility-information-settings-title"
          >
            Facility Information
          </h6>
          <RightOutline className={styles.menuIcon} />
        </div>
        {!hideVitalsAiLink && (
          <div
            className={`${styles.menuItem} mb-3`}
            onClick={() => onRouteChange(routes.vitalSync.path)}
            id="vitals-ai"
          >
            <h6 className={styles.menuItemTitle} id="vitals-ai-title">
              Vitals AI
            </h6>
            <RightOutline className={styles.menuIcon} />
          </div>
        )}
        {circadiaEngineProvider.isWindowCircadia() && isNotAndroid ? (
          <List.Item
            className={`mb-3 ${styles.bioTitle}`}
            arrow={
              <Switch
                data-test-id="biometric-switch"
                disabled={!circadiaEngineProvider.isWindowCircadia()}
                checked={isSwitchBio}
                onChange={(checked) => {
                  setIsSwitchBio(checked);
                  localStorage.setItem("isBiometric", JSON.stringify(checked));
                  if (checked) {
                    analyticsController.logEvent("Biometric Auth enabled", {
                      email: user?.email,
                      selectedFacility: userClinic?.name,
                      selectedFacilityId: userClinic?.id,
                      cUserId: user?.userID,
                    });
                  } else {
                    analyticsController.logEvent("Biometric Auth disabled", {
                      email: user?.email,
                      selectedFacility: userClinic?.name,
                      selectedFacilityId: userClinic?.id,
                      cUserId: user?.userID,
                    });
                  }
                }}
              />
            }
          >
            <div className="d-flex flex-column">
              <h6
                className={`list-name ${styles.menuItemTitle}`}
                id="biometric-title"
              >
                Biometric Auth
              </h6>
            </div>
          </List.Item>
        ) : (
          <></>
        )}
        <Collapse
          className={`mb-3 ${styles.collapse} ${styles.supportFeedback}`}
          style={{ backgroundColor: "white" }}
          arrow={(active) =>
            active ? (
              <DownOutline className={classNames(styles.menuIcon)} />
            ) : (
              <RightOutline className={classNames(styles.menuIcon)} />
            )
          }
        >
          <Collapse.Panel
            key="1"
            title={
              <h6 className={styles.menuItemTitle} id="support-and-feedback">
                Support
              </h6>
            }
            data-test-id="support-and-feedback"
          >
            <div className="d-flex justify-content-between mt-4 text-center">
              <SupportItem
                id="feedback"
                onClick={() => onRouteChange(routes.feedback.path)}
                icon={<MessageOutlined />}
                heading="Feedback"
              />
              <SupportItem
                id="contact-support"
                onClick={() => onRouteChange(routes.contactSupport.path)}
                icon={<CustomerServiceOutlined />}
                heading="Contact Support"
              />
            </div>
            <div className="d-flex justify-content-between mt-4 text-center">
              <SupportItem
                id="rate-app"
                onClick={() => onRouteChange(routes.rateApp.path)}
                icon={<StarOutlined />}
                heading="Rate App"
              />
              <SupportItem
                id="privacy-policy"
                onClick={() => onRouteChange(routes.privacyPolicy.path)}
                icon={<SafetyOutlined />}
                heading="Privacy Policy"
              />
            </div>
            <div className="d-flex justify-content-between mt-4 text-center">
              <SupportItem
                id="intended-use"
                onClick={() => onRouteChange(routes.intendedUse.path)}
                icon={<InfoCircleOutlined />}
                heading="Intended Use"
              />
              <SupportItem
                id="faq"
                onClick={() =>
                  window.open("https://help.circadia.health", "_blank")
                }
                icon={<ExportOutlined />}
                heading="Help Center"
              />
            </div>
          </Collapse.Panel>
        </Collapse>
        <div
          data-test-id="logout"
          className={`${styles.menuItem} mb-3 ${styles.logout}`}
          onClick={onLogout}
        >
          <h6
            className={`${styles.logout} ${styles.LogOutAlignCenter}`}
            id="log-out-title"
          >
            Log Out
          </h6>
          <LogoutOutlined className={styles.logo} />
        </div>
        {showMaskToggle && (
          <div
            className={`${styles.menuItem} mb-3 ${styles.maskToggleContainer}`}
          >
            <div className={styles.maskToggleContent}>
              <span className={styles.maskToggleLabel}>
                Mask patient names to John Doe?
              </span>
              <Switch
                checked={maskPatientNames}
                onChange={handleMaskToggleChange}
                className={styles.maskToggleSwitch}
              />
            </div>
          </div>
        )}
        <div className={styles.footer}>
          <Text className={styles.subText} data-test-id="version">
            Version {APP_VERSION} <br /> All Rights Reserved © Circadia
            Technologies Ltd {year}
          </Text>
        </div>
      </Drawer>
      <ConfirmModal
        subTitle="Are you sure you want to log out?"
        data-test-id="confirmation-modal"
        cancelButtonText="Cancel"
        visible={confirmLogout}
        onCancelClick={onCancel}
        onConfirmClick={onConfirmLogout}
      />
    </>
  );
};
